<template>
	<!--START : Card-->
	<div
		class="card"
		:data-type="type"
		:data-background="background"
		:data-separator="separator"
		:data-clickable="clickable"
		:data-selected="selected"
		:data-disabled="disabled"
	>
		<div v-if="$slots['header']" class="card-header">
			<slot name="header" />
		</div>
		<div class="card-content">
			<slot />
		</div>
		<div v-if="$slots['footer']" class="card-footer">
			<slot name="footer" />
		</div>
		<div v-if="loadingIcon" class="card-loader">
			<Container direction="column" :gap="12" :padding="0" align="center">
				<Icon
					:name="loadingIcon.name"
					:state="loadingIcon.state"
					:type="loadingIcon.type"
					:size="loadingIcon.size"
				/>
				<Typography v-if="loadingText" type="p2" weight="medium" color="dark">
					{{ loadingText }}
				</Typography>
			</Container>
		</div>
	</div>
	<!--END : Card-->
</template>
<script lang="ts">
import { defineComponent, PropType } from "vue";
import Icon, { IconSize, IconState, IconType } from "./../Icon/Icon.vue";
import Container from "./../Container/Container.vue";
import Typography from "./../Typography/Typography.vue";

const TYPE = ["default", "placeholder", "outlined"] as const;
const BACKGROUND = [
	"element",
	"default",
	"element-light",
	"element-dark",
	"font-dark",
	"font",
	"font-light",
	"icon-dark",
	"icon",
	"icon-light",
	"border-dark",
	"border",
	"border-light",
	"primary-dark",
	"primary",
	"primary-light",
	"secondary-dark",
	"secondary",
	"secondary-light",
	"error-dark",
	"error",
	"error-light",
	"warning-dark",
	"warning",
	"warning-light",
	"success-dark",
	"success",
	"success-light",
	"dashboard-background"
] as const;

export type CardBackground = typeof BACKGROUND[number];
export type CardType = typeof TYPE[number];

export default defineComponent({
	name: "Card",
	components: {
		Icon,
		Container,
		Typography
	},
	props: {
		/**
		 * 'default' | 'placeholder'
		 */
		type: {
			type: String as PropType<CardType>,
			default: "default",
			validator(value: CardType) {
				return TYPE.includes(value);
			}
		},
		/**
		 * | 'default' | 'element-light' | 'element' | 'element-dark' | 'font-dark' | 'font' | 'font-light' | 'icon-dark' | 'icon' | 'icon-light' | 'border-dark' | 'border' | 'border-light' | 'primary-dark' | 'primary' | 'primary-light' | 'secondary-dark' | 'secondary' | 'secondary-light' | 'error-dark' | 'error' | 'error-light' | 'warning-dark' | 'warning' | 'warning-light' | 'success-dark' | 'success' | 'success-light' | 'dashboard-background'
		 */
		background: {
			type: String as PropType<CardBackground>,
			default: "default",
			validator(value: CardBackground) {
				return BACKGROUND.includes(value);
			}
		},
		/**
		 * Display separator between header, content, footer <br/>
		 * true | false
		 */
		separator: {
			type: Boolean,
			default: true
		},
		/**
		 * This will add hover effect and cursor-pointer
		 */
		clickable: {
			type: Boolean,
			default: false
		},
		/**
		 * true | false
		 */
		selected: {
			type: Boolean,
			default: false
		},
		/**
		 * true | false
		 */
		loading: {
			type: [Boolean, Object],
			default: false
		},
		/**
		 * Loading text to display below loader
		 */
		loadingText: {
			type: String,
			default: null
		},
		/**
		 * disable card
		 */
		disabled: {
			type: Boolean,
			default: false
		}
	},
	computed: {
		loadingIcon() {
			const icn: { name: string; state: IconState; type: IconType; size: IconSize } = {
				name: "loading",
				state: "error",
				type: "outlined",
				size: "medium"
			};

			if (this.loading && typeof this.loading === "boolean") {
				return icn;
			} else if (this.loading && typeof this.loading === "object") {
				return { ...icn, ...this.loading };
			}

			return null;
		}
	}
});
</script>
<style lang="scss">
@import "../styles/clickable";
/**
*	Card
**/
$backgrounds: (
	"element-light": var(--dashboard-element-light),
	"element": var(--dashboard-element),
	"element-dark": var(--dashboard-element-dark),
	"font-dark": var(--dashboard-font-dark),
	"font": var(--dashboard-font),
	"font-light": var(--dashboard-font-light),
	"icon-dark": var(--dashboard-icon-dark),
	"icon": var(--dashboard-icon),
	"icon-light": var(--dashboard-icon-light),
	"border-dark": var(--dashboard-border-dark),
	"border": var(--dashboard-border),
	"border-light": var(--dashboard-border-light),
	"primary-dark": var(--dashboard-primary-dark),
	"primary": var(--dashboard-primary),
	"primary-light": var(--dashboard-primary-light),
	"secondary-dark": var(--dashboard-secondary-dark),
	"secondary": var(--dashboard-secondary),
	"secondary-light": var(--dashboard-secondary-light),
	"error-dark": var(--dashboard-danger-dark),
	"error": var(--dashboard-danger),
	"error-light": var(--dashboard-danger-light),
	"warning-dark": var(--dashboard-warning-dark),
	"warning": var(--dashboard-warning),
	"warning-light": var(--dashboard-warning-light),
	"success-dark": var(--dashboard-success-dark),
	"success": var(--dashboard-success),
	"success-light": var(--dashboard-success-light),
	"dashboard-background": var(--dashboard-background),
	"default": transparent
);
$card-border-radius: 4px;
$card-background-color: var(--dashboard-element-light);
$card-spacing: 12px;
$card-border-color: var(--dashboard-border-dark);

div.card {
	display: flex;
	flex-direction: column;
	border-radius: $card-border-radius;
	height: 100%;
	overflow: hidden;
	flex: 1;

	position: relative;
	// * {
	// 	z-index: 2;
	// }
	> div.card-header {
		display: flex;
		align-items: center;
		min-height: 36px;

		> div {
			height: 100%;
		}
		> * + * {
			margin-left: $card-spacing;
		}
		flex-grow: 0;
		> div.container {
			width: 100%;
		}

		div.slab-hierarchy > div.slab-container > div.slab-secondary-actions {
			align-self: flex-start;
		}
	}
	> div.card-footer {
		min-height: 36px;
		flex-grow: 0;
		display: flex;
		flex-direction: column;
		justify-items: stretch;
		overflow: hidden;
		border-bottom-left-radius: $card-border-radius;
		border-bottom-right-radius: $card-border-radius;
		> div,
		> button[state="full"] {
			height: 100%;
			min-height: inherit;
		}

		.flow-popover-content div.slab-hierarchy > div.slab-container {
			> div.slab-content {
				> div.slab-primary-action {
					padding-left: 12px;
				}
				> .slab-title {
					padding-left: 12px;
				}
			}
		}
	}
	> div.card-content {
		flex: 1;
		overflow: auto;
	}
	&.placeholder {
		border: 1px dashed $card-border-color;
		background-color: transparent;
	}

	@each $name, $background in $backgrounds {
		&[data-background="#{$name}"] {
			background-color: $background;
		}
	}

	&[data-type="placeholder"] {
		border: 2px dashed $card-border-color;

		> div.card-header {
			border-bottom: none;
		}
	}

	&[data-type="outlined"] {
		border: 1px solid $card-border-color;
	}
	&[data-separator] {
		> div.card-header {
			border-bottom: 1px solid $card-border-color;
		}
		> div.card-footer {
			border-top: 1px solid $card-border-color;
		}
	}
	&[data-separator][data-type="placeholder"] {
		> div.card-header {
			border-bottom: 2px dashed $card-border-color;
		}
		> div.card-footer {
			border-top: 2px dashed $card-border-color;
		}
	}
	&[data-clickable] {
		@include clickable();
	}
	&[data-selected] {
		box-shadow: inset 0 0 0 1px var(--dashboard-primary);
		outline: none;
	}

	&[data-disabled] {
		opacity: 0.4;
		pointer-events: none;
	}

	.card-loader {
		position: absolute;
		top: 0px;
		bottom: 0px;
		left: 0px;
		right: 0px;
		border-radius: $card-border-radius;
		background: rgba(0, 0, 0, 0.7);
		display: flex;
		align-items: center;
		justify-content: center;
	}
}
</style>
